// v10/src/components/admin/inventory/InventoryItemCard.js
//
// Card individual de un producto del inventario (A8) — Tarea 1 (lectura).
// Foto si existe + placeholder de la categoría si no, stock con unidad,
// badge de "Stock bajo" cuando aplica, contadores de compatibilidades y S/N.
//
// El kebab menu tiene placeholders con window.alert hasta que la Tarea 2
// conecte los modales reales (edit / movement / view detail / delete).

import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MoreVertical, AlertTriangle, Hash, Plane,
  Edit3, Trash2, ArrowRightLeft, Eye, Image as ImageIcon, Camera,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getCategoryIcon } from './categoryIcons';
import { useToast } from '../../../hooks/useToast';

// Helpers de formato — alineados con la convención es-AR del proyecto:
// separador miles con punto, decimales con coma.
const formatStock = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  // Mostramos hasta 3 decimales para no perder precisión en mediciones tipo
  // litros / gramos pero ocultamos los ceros sobrantes.
  return num.toLocaleString(locale, { maximumFractionDigits: 3, minimumFractionDigits: 0 });
};

const InventoryItemCard = ({
  item, darkMode, locale = 'es-AR', onAction,
  canEdit = true, canDelete = true,
}) => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const Icon = getCategoryIcon(item?.category);

  const isLowStock = item?.is_low_stock === true;
  const isSerialized = item?.tracking_mode === 'serialized';
  const compatCount = Number(item?.compatibilities_count) || 0;
  const isUniversal = compatCount === 0;
  const serializedCount = Number(item?.serialized_count) || 0;

  const stockUnitLabel = item?.stock_unit
    ? t(`inventory_units_${item.stock_unit}`, { defaultValue: item.stock_unit })
    : '';
  const purchaseUnitLabel = item?.purchase_unit
    ? t(`inventory_purchase_units_${item.purchase_unit}`, { defaultValue: item.purchase_unit })
    : '';

  const categoryLabel = item?.category
    ? t(`inventory_categories_${item.category}`, { defaultValue: item.category })
    : '';

  const subtitleParts = [];
  if (categoryLabel) subtitleParts.push(categoryLabel);
  if (item?.manufacturer) subtitleParts.push(item.manufacturer);
  const subtitle = subtitleParts.join(' · ');

  const cardBg     = darkMode ? 'rgba(255,255,255,0.03)' : 'white';
  const cardBorder = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const fgPrimary  = darkMode ? 'white' : '#0f172a';
  const fgMuted    = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const fgFaint    = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';

  const accentColor = isLowStock ? '#ea580c' : '#6366f1';
  const accentBg    = isLowStock ? 'rgba(234,88,12,0.14)' : 'rgba(99,102,241,0.14)';

  const handleAction = (action) => {
    setMenuOpen(false);
    if (onAction) {
      onAction(action, item);
    } else {
      // Fallback si el card se monta sin onAction conectado.
      toast.info(t('inventory_action_placeholder'));
    }
  };

  // Stock formateado con unidad. Mostramos también el mínimo si está configurado.
  const currentStockText = `${formatStock(item?.current_stock_quantity, locale)} ${stockUnitLabel}`.trim();
  const minStockNumber = Number(item?.minimum_stock_quantity);
  const showMin = Number.isFinite(minStockNumber) && minStockNumber > 0;
  const minStockText = showMin
    ? `${formatStock(minStockNumber, locale)} ${stockUnitLabel}`.trim()
    : null;

  // Equivalencia de paquete (12 botellas = 1 caja, etc.)
  const upp = Number(item?.units_per_purchase);
  const showPurchaseHint = Number.isFinite(upp) && upp > 0 && !!item?.purchase_unit;

  const compatibleLabel = isUniversal
    ? t('inventory_item_card_universal_badge')
    : t('inventory_item_card_compatible_aircraft', { count: compatCount });

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      style={{
        background: cardBg,
        border: cardBorder,
        borderRadius: 20,
        padding: 16,
        boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
        position: 'relative',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* Foto o placeholder */}
        <div
          style={{
            width: 56, height: 56, borderRadius: 14,
            background: accentBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, overflow: 'hidden',
          }}
          aria-hidden="true"
        >
          {item?.photo_url ? (
            <img
              src={item.photo_url}
              alt={item?.name || ''}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Icon style={{ width: 26, height: 26, color: accentColor }} />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              margin: 0, fontSize: 15, fontWeight: 800, color: fgPrimary,
              letterSpacing: '-0.2px', lineHeight: 1.25,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
            title={item?.name}
          >
            {item?.name}
          </h3>
          {subtitle && (
            <p
              style={{
                margin: '4px 0 0', fontSize: 12, color: fgMuted, lineHeight: 1.35,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
              title={subtitle}
            >
              {subtitle}
            </p>
          )}
        </div>

        {/* Kebab menu */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setMenuOpen((p) => !p)}
            aria-label={t('inventory_item_card_actions_menu')}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 6, borderRadius: 10, display: 'flex',
              color: fgMuted,
            }}
          >
            <MoreVertical style={{ width: 18, height: 18 }} />
          </button>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.12 }}
                style={{
                  position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                  minWidth: 220,
                  background: darkMode ? '#1f2937' : 'white',
                  border: cardBorder,
                  borderRadius: 14, padding: 6,
                  boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                  zIndex: 30,
                }}
              >
                <MenuItem
                  icon={Eye}
                  label={t('inventory_item_card_action_view_detail')}
                  onClick={() => handleAction('view_detail')}
                  darkMode={darkMode}
                />
                {canEdit && (
                  <MenuItem
                    icon={ArrowRightLeft}
                    label={t('inventory_item_card_action_movement')}
                    onClick={() => handleAction('movement')}
                    darkMode={darkMode}
                  />
                )}
                {canEdit && (
                  <MenuItem
                    icon={Edit3}
                    label={t('inventory_item_card_action_edit')}
                    onClick={() => handleAction('edit')}
                    darkMode={darkMode}
                  />
                )}
                {canEdit && (
                  <MenuItem
                    icon={item?.photo_url ? Camera : ImageIcon}
                    label={item?.photo_url
                      ? t('inventory_item_card_action_replace_photo')
                      : t('inventory_item_card_action_upload_photo')}
                    onClick={() => handleAction('photo')}
                    darkMode={darkMode}
                  />
                )}
                {canEdit && isSerialized && (
                  <MenuItem
                    icon={Hash}
                    label={t('inventory_item_card_action_manage_serialized')}
                    onClick={() => handleAction('manage_serialized')}
                    darkMode={darkMode}
                  />
                )}
                {canEdit && (
                  <MenuItem
                    icon={Plane}
                    label={t('inventory_item_card_action_compatibilities')}
                    onClick={() => handleAction('compatibilities')}
                    darkMode={darkMode}
                  />
                )}
                {canDelete && (
                  <MenuItem
                    icon={Trash2}
                    label={t('inventory_item_card_action_delete')}
                    onClick={() => handleAction('delete')}
                    darkMode={darkMode}
                    danger
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Stock */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: fgFaint, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {t('inventory_item_card_stock_label')}
          </p>
          <p
            style={{
              margin: '4px 0 0', fontSize: 18, fontWeight: 900, color: fgPrimary,
              letterSpacing: '-0.3px', lineHeight: 1.1,
            }}
          >
            {currentStockText}
          </p>
          {minStockText && (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: fgMuted }}>
              {t('inventory_item_card_minimum_label')}: {minStockText}
            </p>
          )}
        </div>

        {isLowStock && (
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 11, fontWeight: 800,
              padding: '5px 10px', borderRadius: 999,
              background: 'rgba(234,88,12,0.14)', color: '#ea580c',
              letterSpacing: '0.4px', textTransform: 'uppercase',
            }}
          >
            <AlertTriangle style={{ width: 12, height: 12 }} />
            {t('inventory_item_card_low_stock_badge')}
          </span>
        )}
      </div>

      {/* Hint paquete (12 botellas = 1 caja) */}
      {showPurchaseHint && (
        <p style={{ margin: 0, fontSize: 11.5, color: fgFaint }}>
          {t('inventory_item_card_purchase_unit_hint', {
            units: formatStock(upp, locale),
            stock_unit: stockUnitLabel,
            purchase_unit: purchaseUnitLabel,
          })}
        </p>
      )}

      {/* Footer: badges */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          paddingTop: 10,
          borderTop: darkMode ? '1px dashed rgba(255,255,255,0.08)' : '1px dashed #e2e8f0',
        }}
      >
        <span
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, fontWeight: 700,
            padding: '4px 9px', borderRadius: 999,
            background: isUniversal
              ? (darkMode ? 'rgba(99,102,241,0.16)' : 'rgba(99,102,241,0.10)')
              : (darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9'),
            color: isUniversal
              ? '#6366f1'
              : (darkMode ? 'rgba(255,255,255,0.75)' : '#475569'),
          }}
        >
          <Plane style={{ width: 12, height: 12 }} />
          {compatibleLabel}
        </span>

        {isSerialized && (
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 11, fontWeight: 700,
              padding: '4px 9px', borderRadius: 999,
              background: 'rgba(20,184,166,0.14)', color: '#0d9488',
            }}
            title={t('inventory_item_card_serialized_badge')}
          >
            <Hash style={{ width: 12, height: 12 }} />
            {serializedCount > 0 ? `${serializedCount} S/N` : 'S/N'}
          </span>
        )}
      </div>
    </motion.div>
  );
};

const MenuItem = ({ icon: Icon, label, onClick, darkMode, danger = false }) => {
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
        background: 'transparent', color: danger ? '#ef4444' : fgPrimary,
        fontFamily: 'inherit', fontSize: 13, fontWeight: 600, textAlign: 'left',
      }}
    >
      <Icon style={{ width: 15, height: 15 }} />
      {label}
    </button>
  );
};

export default InventoryItemCard;
